// Per-profile UI preferences — non-clinical view settings scoped to each patient
// profile, stored in a dedicated Firestore doc so they follow the user across devices
// and never mix with PHI or the clinical record.
//
//   users/{ownerUid}/profiles/{pid}/prefs/labView   (a plain map, not encrypted)
//
// Scalability: to add a new preference, add a key here and a line in stores.ts's
// applyPrefs()/snapshot(). One doc, one read on profile activation, debounced writes.
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { fbDb } from './firebase';

export interface ProfilePrefs {
  dashLabs?: string[];    // ≤4 lab markers pinned to the dashboard
  trendsLabs?: string[];  // ≤4 extra markers added on the Trends page
  trendsOrder?: string[]; // user-arranged display order of the Trends charts
  // ↑ extend here; loaders tolerate missing/unknown keys.
}

const prefsRef = (ownerUid: string, pid: string) => doc(fbDb, 'users', ownerUid, 'profiles', pid, 'prefs', 'labView');

/** Read a profile's saved preferences ({} if none / on error — never throws). */
export async function loadProfilePrefs(ownerUid: string, pid: string): Promise<ProfilePrefs> {
  try { const s = await getDoc(prefsRef(ownerUid, pid)); return s.exists() ? (s.data() as ProfilePrefs) : {}; }
  catch { return {}; }
}

/** Merge a preferences patch into the profile's prefs doc (best-effort). */
export async function saveProfilePrefs(ownerUid: string, pid: string, patch: Partial<ProfilePrefs>): Promise<void> {
  try { await setDoc(prefsRef(ownerUid, pid), patch, { merge: true }); } catch (_) {}
}

// ── Account-level appearance preferences (per USER, not per profile) — theme, font,
// text size. Stored on the account doc (users/{uid}.app) so a person's chosen look
// follows them across devices regardless of which patient profile is open. ──────────
export interface AppPrefs {
  theme?: string;      // 'dark' | 'light' | 'system'
  font?: string;       // typeface id
  fontScale?: number;  // text-size level 0..3
}
const accountRef = (uid: string) => doc(fbDb, 'users', uid);
export async function loadAppPrefs(uid: string): Promise<AppPrefs> {
  try { const s = await getDoc(accountRef(uid)); const d = s.exists() ? (s.data() as { app?: AppPrefs }) : null; return d?.app ?? {}; }
  catch { return {}; }
}
export async function saveAppPrefs(uid: string, patch: Partial<AppPrefs>): Promise<void> {
  try { await setDoc(accountRef(uid), { app: patch }, { merge: true }); } catch (_) {}
}
